import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useSession } from '@/shared/auth';
import { supabase } from '@/lib/supabase';
import { isAllowedViewMode, isViewModeActive, type Membership, type OrgRole } from './types';

/**
 * Which organization the user is acting as, for the whole app.
 *
 * The tenant is the organization, never the user (CLAUDE.md). A solo
 * professional is not a special case: signup gives them a personal org with one
 * owner membership, so there is exactly one shape here for everyone.
 *
 * Only ever read `orgId` from this provider. A component that takes an org id
 * from a prop, a route param or a query string is one refactor away from
 * letting a caller name someone else's tenant — and while RLS would still deny
 * the read, the bug surfaces as mysterious emptiness instead of a clear error.
 */
export type OrgState =
  | { readonly status: 'loading' }
  | { readonly status: 'signedOut' }
  /** Signed in but belonging to no organization — only reachable if the signup
   *  trigger failed, so it is surfaced rather than papered over. */
  | { readonly status: 'noOrg' }
  | {
      readonly status: 'ready';
      readonly orgId: string;
      readonly membership: Membership;
      readonly memberships: readonly Membership[];
      readonly role: OrgRole;
      readonly actualRole: OrgRole;
      readonly activeViewMode: OrgRole | null;
      readonly setViewMode: (mode: OrgRole | null) => void;
      readonly isViewingAs: boolean;
      readonly viewAs: ViewAsMember | null;
      readonly startViewingAs: (member: ViewAsMember) => Promise<boolean>;
      readonly stopViewingAs: () => void;
      readonly setActiveOrg: (orgId: string) => void;
      /**
       * Re-read the membership list. Needed after creating an organization:
       * the row exists and the founder trigger has already granted ownership,
       * but this provider only loads when the user changes, so without it the
       * switcher would not show what the user just made.
       */
      readonly reload: () => void;
    };

export interface ViewAsMember {
  readonly userId: string;
  readonly name: string | null;
  readonly email: string | null;
  readonly role: OrgRole;
}

export const OrgContext = createContext<OrgState | null>(null);

interface MembershipRow {
  id: string;
  org_id: string;
  user_id: string;
  role: string;
  team_id: string | null;
  organizations: {
    id: string;
    slug: string;
    public_slug: string | null;
    name: string;
    kind: string;
    is_platform: boolean;
  } | null;
}

function toMembership(row: MembershipRow): Membership | null {
  const org = row.organizations;
  if (!org) return null;
  return {
    id: row.id,
    orgId: row.org_id,
    userId: row.user_id,
    role: row.role as OrgRole,
    teamId: row.team_id,
    organization: {
      id: org.id,
      slug: org.slug,
      publicSlug: org.public_slug,
      name: org.name,
      kind: org.kind === 'company' ? 'company' : 'personal',
      isPlatform: org.is_platform,
    },
  };
}

export function OrgProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  const userId = session.session?.user.id ?? null;

  const [memberships, setMemberships] = useState<readonly Membership[] | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [viewAs, setViewAs] = useState<ViewAsMember | null>(null);
  const [activeViewMode, setActiveViewMode] = useState<OrgRole | null>(null);

  const currentMembership =
    memberships && activeId
      ? (memberships.find((m) => m.orgId === activeId) ?? memberships[0] ?? null)
      : null;

  useEffect(() => {
    if (!activeId || !currentMembership) {
      setActiveViewMode(null);
      return;
    }
    try {
      const stored = localStorage.getItem(`numtable_view_mode_${activeId}`);
      if (stored && isAllowedViewMode(currentMembership.role, stored as OrgRole)) {
        setActiveViewMode(stored as OrgRole);
      } else {
        if (stored) localStorage.removeItem(`numtable_view_mode_${activeId}`);
        setActiveViewMode(null);
      }
    } catch {
      setActiveViewMode(null);
    }
  }, [activeId, currentMembership?.role]);

  useEffect(() => {
    if (!userId) {
      setMemberships(null);
      setActiveId(null);
      setViewAs(null);
      setActiveViewMode(null);
      return;
    }

    let active = true;

    void (async () => {
      // No `user_id` filter: RLS already limits this to the caller's own
      // memberships, and filtering here would only duplicate that.
      const { data, error } = await supabase
        .from('memberships')
        .select('id, org_id, user_id, role, team_id, organizations(id, slug, public_slug, name, kind, is_platform)')
        .eq('status', 'active');

      if (!active) return;
      if (error) {
        setMemberships([]);
        return;
      }

      const rows = ((data ?? []) as MembershipRow[])
        .map(toMembership)
        .filter((m): m is Membership => m !== null)
        // Personal orgs last: someone who belongs to a company is usually there
        // to work, not to look at their own single-person tenant.
        .sort((a, b) => {
          if (a.organization.kind !== b.organization.kind) {
            return a.organization.kind === 'company' ? -1 : 1;
          }
          return a.organization.name.localeCompare(b.organization.name);
        });

      setMemberships(rows);

      // `profiles.last_org_id` is the remembered choice; fall back to the first
      // membership so a fresh account lands somewhere real.
      const { data: profile } = await supabase
        .from('profiles')
        .select('last_org_id')
        .eq('id', userId)
        .maybeSingle();

      if (!active) return;
      const remembered = profile?.last_org_id ?? null;
      const known = rows.some((m) => m.orgId === remembered) ? remembered : null;
      // A reload must not undo a choice made a moment ago: `last_org_id` is
      // written best-effort and can still be in flight when the list refreshes
      // after creating an organization.
      setActiveId((current) =>
        current && rows.some((m) => m.orgId === current)
          ? current
          : (known ?? rows[0]?.orgId ?? null),
      );
    })();

    return () => {
      active = false;
    };
  }, [userId, nonce]);

  useEffect(() => { setViewAs(null); }, [activeId]);

  useEffect(() => {
    if (!userId || !memberships || activeId === null) return;
    let active = true;
    void supabase.auth.getSession().then(({ data }) => fetch('/api/view-as/status', {
      headers: { ...(data.session ? { Authorization: `Bearer ${data.session.access_token}` } : {}), 'X-Active-Org': activeId },
    })).then(async (response) => {
      if (!active || !response.ok) return;
      const body = await response.json() as { active?: boolean; orgId?: string; target?: ViewAsMember };
      if (body.active && body.orgId === activeId && body.target) setViewAs(body.target);
    }).catch(() => undefined);
    return () => { active = false; };
  }, [activeId, memberships, userId]);

  const setActiveOrg = useCallback(
    (orgId: string) => {
      setActiveId(orgId);
      if (!userId) return;
      // Remembering the choice is best-effort: failing to persist it must not
      // block the switch the user just made.
      void supabase.from('profiles').update({ last_org_id: orgId }).eq('id', userId);
    },
    [userId],
  );

  const setViewMode = useCallback(
    (mode: OrgRole | null) => {
      if (!activeId || !currentMembership) return;
      if (mode === null) {
        try {
          localStorage.removeItem(`numtable_view_mode_${activeId}`);
        } catch {}
        setActiveViewMode(null);
        return;
      }
      // Picking the row that stands for your own role means "stop previewing",
      // not "pin me to it". An owner is labelled Developer and that row is the
      // one the menu already shows as checked, so selecting it used to store a
      // developer override — demoting a real owner, disabling every org setting,
      // and leaving no way back, because the list holds no Owner row that could
      // return the override to null. `isViewModeActive` is the same predicate
      // the menu uses to draw the checkmark, so the row that looks active is by
      // construction the row that clears.
      if (isViewModeActive(mode, null, currentMembership.role)) {
        try {
          localStorage.removeItem(`numtable_view_mode_${activeId}`);
        } catch {}
        setActiveViewMode(null);
        return;
      }
      if (isAllowedViewMode(currentMembership.role, mode)) {
        try {
          localStorage.setItem(`numtable_view_mode_${activeId}`, mode);
        } catch {}
        setActiveViewMode(mode);
      }
    },
    [activeId, currentMembership?.role],
  );

  const value = useMemo<OrgState>(() => {
    if (session.status === 'loading') return { status: 'loading' };
    if (session.status === 'signedOut') return { status: 'signedOut' };
    if (memberships === null || activeId === null) {
      return memberships !== null && memberships.length === 0
        ? { status: 'noOrg' }
        : { status: 'loading' };
    }

    const membership =
      memberships.find((m) => m.orgId === activeId) ?? memberships[0];
    if (!membership) return { status: 'noOrg' };

    const actualRole = membership.role;
    const canViewAs = actualRole === 'owner' || actualRole === 'developer';
    const effectiveViewAs = canViewAs ? viewAs : null;
    const effectiveViewMode =
      !effectiveViewAs && activeViewMode && isAllowedViewMode(actualRole, activeViewMode)
        ? activeViewMode
        : null;
    const effectiveRole = effectiveViewAs?.role ?? effectiveViewMode ?? actualRole;

    return {
      status: 'ready',
      orgId: membership.orgId,
      membership,
      memberships,
      role: effectiveRole,
      actualRole,
      activeViewMode: effectiveViewMode,
      setViewMode,
      isViewingAs: effectiveViewAs !== null,
      viewAs: effectiveViewAs,
      startViewingAs: async (member: ViewAsMember) => {
        if (!canViewAs || member.userId === userId) return false;
        const { data } = await supabase.auth.getSession();
        const response = await fetch('/api/view-as/start', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Active-Org': membership.orgId, ...(data.session ? { Authorization: `Bearer ${data.session.access_token}` } : {}) },
          body: JSON.stringify({ targetUserId: member.userId }),
        }).catch(() => null);
        if (!response?.ok) return false;
        setViewAs(member);
        return true;
      },
      stopViewingAs: () => {
        setViewAs(null);
        void supabase.auth.getSession().then(({ data }) => fetch('/api/view-as/stop', {
          method: 'POST',
          headers: { 'X-Active-Org': membership.orgId, ...(data.session ? { Authorization: `Bearer ${data.session.access_token}` } : {}) },
        }).catch(() => undefined));
      },
      setActiveOrg,
      reload: () => setNonce((n) => n + 1),
    };
  }, [session.status, memberships, activeId, setActiveOrg, userId, viewAs, activeViewMode, setViewMode]);

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}
